/* Normalised garment coordinates, inspired by the recovered native personaliser.
   Flatlays and fonts are theme/CDN assets. No Mono service or lifestyle fallback. */
(() => {
  const fonts = [{font_name:'Block',font_code:'garmnd'},{font_name:'Cursive',font_code:'bluebell_2'},{font_name:'Serif',font_code:'garamond_regular'}];
  const threads = [['White','#ffffff'],['Black','#000000'],['Silver','#c0c0c0'],['Gold','#d3af38'],['Dusty Rose','#c17c71'],['French Navy','#121e41'],['Eggshell Blue','#d6edfc'],['Blush','#e8b9b4'],['Dove Grey','#c6bdb3'],['Carnation','#ed1651'],['Peach','#f4b4a5'],['Plum','#5b1a33'],['Cranberry','#961939'],['Apple','#da1f2b'],['Charcoal','#4a4e48'],['Lilac','#af9bc4'],['Yellow','#efe3af']].map(([colour_name,colour_code]) => ({colour_name,colour_code,is_enabled:1}));
  const images = new Map();
  const canonical = code => code === 'kids-centre-pocket' ? 'centre_of_pocket' : code;
  // Shopify Files can use either the shop CDN or storefront /cdn/shop URL.
  // Compare the file path so a transformed URL cannot bypass a blocked mapping.
  const identity = url => {
    try {
      const path = new URL(url, location.origin).pathname;
      const file = path.match(/^\/(?:cdn\/shop|s\/files\/\d+\/\d+\/\d+\/\d+)\/files\/(.+)$/);
      return file ? `/files/${decodeURIComponent(file[1])}` : path;
    } catch (_) { return ''; }
  };
  const safeImage = url => { if (typeof url !== 'string' || !url.trim()) return null; try { const u = new URL(url, location.origin); return u.protocol === 'https:' && (u.hostname === 'cdn.shopify.com' || u.hostname === location.hostname) ? u.href : null; } catch (_) { return null; } };
  function resolve(config, variantId) {
    const entry = window.HBPersonalisationCatalog[String(config.id)];
    const productLayout = config.layout || {};
    const variant = config.variants[variantId];
    if (!variant || !variant.available || productLayout.enabled === false) return {placements:[]};
    const variantLayout = variant.layout || {};
    if (variantLayout.enabled === false) return {placements:[]};
    const coordinates = Array.isArray(productLayout.placements) ? productLayout.placements.map(code => ({area_code:code,area_name:code.replaceAll('_',' '),allowed_fonts:fonts,char_limit:8,line_limit:1})) : entry?.coordinates || [];
    const placements = window.HBPersonalisationRules.placements(coordinates, `${config.title} ${config.type}`).flatMap(p => {
      const code = canonical(p.area_code);
      const defaults = config.positions.filter(b => b.code === code && !b.products.length).at(-1);
      const productBlock = config.positions.filter(b => b.code === code && b.products.map(String).includes(String(config.id))).at(-1);
      const layout = {...defaults,...productBlock,...productLayout[code],...variantLayout[code]};
      if (layout.enabled === false || !defaults && !productBlock && !productLayout[code]) return [];
      const view = layout.view || 'front';
      const image = safeImage(variant.images[view] || (!config.requiresVariantImage ? config.images[view] : null));
      if (!image) return [];
      if (entry?.blocked && (entry.blockedImages || [entry.blockedImage]).some(url => identity(image) === identity(url))) return [];
      if (![layout.x,layout.y,layout.size,layout.maxWidth,layout.rotation].every(Number.isFinite)) return [];
      if (layout.x < 0 || layout.x > 100 || layout.y < 0 || layout.y > 100 || layout.size <= 0 || layout.size > 30 || layout.maxWidth <= 0 || layout.maxWidth > 100 || Math.abs(layout.rotation)>180) return [];
      return [{...p,layout:{image,view,x:layout.x,y:layout.y,size:layout.size,maxWidth:layout.maxWidth,rotation:layout.rotation}}];
    });
    return {placements};
  }
  function hydrate(selection, placements) {
    if (!selection) return null;
    const p = placements.find(p => p.area_code === selection.placementCode);
    const f = p?.allowed_fonts.find(f => f.font_code === selection.fontCode);
    const c = threads.find(c => c.colour_code === selection.colorHex);
    const text = window.HBPersonalisationRules.text(String(selection.text || ''), p).trim();
    if (!p || !f || !c || !text) return null;
    return {text,font:f.font_name.toLowerCase(),fontCode:f.font_code,fontFamily:`"HB-${f.font_code}"`,color:c.colour_name,colorHex:c.colour_code,placement:p.area_name,placementCode:p.area_code,charLimit:p.char_limit,lineLimit:p.line_limit,layout:{...p.layout}};
  }
  function loadImage(url) {
    if (!images.has(url)) images.set(url,new Promise((resolve,reject) => {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => { images.delete(url); reject(new Error('The flatlay could not load. Please try again.')); };
      img.src = url;
    }));
    return images.get(url);
  }
  async function render(designs) {
    const image = await loadImage(designs[0].layout.image);
    for (const d of designs) {
      if (!(await document.fonts.load(`32px "HB-${d.fontCode}"`)).length) throw new Error('The embroidery font could not load. Please try again.');
    }
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image,0,0);
    for (const design of designs) {
      const p = design.layout;
      if (p.image !== designs[0].layout.image) continue;
      let size = canvas.width * p.size / 100;
      ctx.font = `${size}px "HB-${design.fontCode}"`;
      const measured = ctx.measureText(design.text).width;
      size *= Math.min(1,canvas.width * p.maxWidth / 100 / Math.max(measured,1));
      ctx.save();
      ctx.translate(canvas.width*p.x/100,canvas.height*p.y/100);
      ctx.rotate(p.rotation*Math.PI/180);
      ctx.font = `${size}px "HB-${design.fontCode}"`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = design.colorHex;
      ctx.fillText(design.text,0,0);
      ctx.restore();
    }
    return canvas.toDataURL('image/jpeg',0.92);
  }
  async function attachments(designs) {
    const views = [...new Set(designs.map(d => d.layout.image))];
    const files = [];
    for (const [index, image] of views.entries()) {
      const matching = designs.filter(d => d.layout.image === image);
      const data = await render(matching);
      const binary = atob(data.split(',')[1]);
      const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
      files.push({key:index === 0 ? '_image_url' : `_personalisation_preview_${index + 1}`, file:new File([bytes], `homebodii-${matching[0].layout.view}-${index + 1}.jpg`, {type:'image/jpeg'})});
    }
    return files;
  }
  window.HBPersonalisationLocal = {resolve,hydrate,render,attachments,threads};
})();
